import { CriticalProfilePoint } from 'features/indicationTriage/cards/criticalProfile/criticalProfileApi'
import { WORKING_UNIT_THICKNESS } from 'features/indicationTriage/cards/ffsCalc/ffsUnits'
import Plot from 'features/plotly'
import isNil from 'lodash/isNil'
import { Data as PlotData, Layout } from 'plotly.js'
import React, { FC, useMemo } from 'react'
import CantileverTheme from 'styles/cantileverTheme'
import { GeckoMeasurement } from 'types'

const PLOTLY_FG_COLOR = CantileverTheme.colors.grayscale[100]
const PLOTLY_FONT = {
  color: PLOTLY_FG_COLOR,
}

// Color constants
const COLORS = {
  criticalPath: '#6B7280',
  deepestPoint: '#EF4444',
  average: '#10B981',
  tnom: '#374151',
  trd: '#6B7280',
  tc: '#3B82F6',
  fcaMl: '#9CA3AF',
  lBoundary: '#3B82F6',
}

// Marker configurations
const MARKERS = {
  criticalPath: {
    size: 4,
    color: COLORS.criticalPath,
  },
  deepestPoint: {
    size: 12,
    color: COLORS.deepestPoint,
    symbol: 'circle' as const,
    line: { width: 2, color: '#fff' },
  },
}

// Line configurations
const LINES = {
  criticalPath: {
    color: COLORS.criticalPath,
    width: 2,
  },
  average: {
    color: COLORS.average,
    width: 3,
    dash: 'dash' as const,
  },
  lBoundary: {
    color: COLORS.lBoundary,
    width: 2,
    dash: 'dash' as const,
  },
}

// Hover template builder
const buildHoverTemplate = (title?: string): string => {
  const titleLine = title ? `<b>${title}</b><br>` : ''
  return (
    titleLine +
    `<b>Position:</b> %{x:.2f} ${WORKING_UNIT_THICKNESS.name}<br>` +
    `<b>Thickness:</b> %{y:.2f} ${WORKING_UNIT_THICKNESS.name}<br>` +
    '<extra></extra>'
  )
}

type PlotOverlay = {
  shapes: Partial<Plotly.Shape>[]
  annotations: Partial<Plotly.Annotations>[]
}

const createFullHorizontalLine = (
  xMin: number,
  xMax: number,
  leftExtension: number,
  rightExtension: number,
  value: number | null | undefined,
  color: string,
  dash: 'solid' | 'dash' | 'dot',
  label: string
): PlotOverlay | null => {
  if (isNil(value)) return null

  return {
    shapes: [
      {
        type: 'line' as const,
        x0: xMin - leftExtension,
        x1: xMax + rightExtension,
        y0: value,
        y1: value,
        line: { color, width: 2, dash },
        layer: 'below' as const,
      },
    ],
    annotations: [
      {
        x: xMax + rightExtension,
        y: value,
        text: label,
        showarrow: false,
        xanchor: 'left' as const,
        xshift: 5,
        font: { size: 10, color },
      },
    ],
  }
}

const createSplitHorizontalLine = (
  xMin: number,
  xMax: number,
  leftExtension: number,
  rightExtension: number,
  value: number | null | undefined,
  color: string,
  dash: 'solid' | 'dash' | 'dot',
  label: string
): PlotOverlay | null => {
  if (isNil(value)) return null

  return {
    shapes: [
      {
        type: 'line' as const,
        x0: xMin - leftExtension,
        x1: xMin,
        y0: value,
        y1: value,
        line: { color, width: 2, dash },
        layer: 'below' as const,
      },
      {
        type: 'line' as const,
        x0: xMax,
        x1: xMax + rightExtension,
        y0: value,
        y1: value,
        line: { color, width: 2, dash },
        layer: 'below' as const,
      },
    ],
    annotations: [
      {
        x: xMax + rightExtension,
        y: value,
        text: label,
        showarrow: false,
        xanchor: 'left' as const,
        xshift: 5,
        font: { size: 10, color },
      },
    ],
  }
}

const createBoundaryLines = (
  leftBound: GeckoMeasurement | null,
  rightBound: GeckoMeasurement | null,
  tnom: GeckoMeasurement | null
): PlotOverlay | null => {
  if (isNil(leftBound) || isNil(rightBound) || isNil(tnom)) return null

  const leftX = leftBound.convertTo(WORKING_UNIT_THICKNESS).num
  const rightX = rightBound.convertTo(WORKING_UNIT_THICKNESS).num
  const maxY = tnom.convertTo(WORKING_UNIT_THICKNESS).num * 1.1

  return {
    shapes: [
      {
        type: 'line' as const,
        x0: leftX,
        x1: leftX,
        y0: 0,
        y1: maxY,
        line: LINES.lBoundary,
        layer: 'below' as const,
      },
      {
        type: 'line' as const,
        x0: rightX,
        x1: rightX,
        y0: 0,
        y1: maxY,
        line: LINES.lBoundary,
        layer: 'below' as const,
      },
    ],
    annotations: [
      {
        x: leftX,
        y: 0,
        text: 'L/2 ←',
        showarrow: false,
        yshift: -20,
        font: { size: 10, color: COLORS.lBoundary },
      },
      {
        x: rightX,
        y: 0,
        text: '→ L/2',
        showarrow: false,
        yshift: -20,
        font: { size: 10, color: COLORS.lBoundary },
      },
    ],
  }
}

export interface FfsCtpPlotProps {
  criticalPath: ReadonlyArray<CriticalProfilePoint>
  axis: 'x' | 'y'
  axisLabel: string
  l: GeckoMeasurement | null

  // Thickness measurements
  tnom: GeckoMeasurement | null
  trd: GeckoMeasurement | null
  tc: GeckoMeasurement | null
  tmm: GeckoMeasurement | null
  fca: GeckoMeasurement | null
  fcaMl: GeckoMeasurement | null

  // Calculated values
  minPointIndex: number
  leftBound: GeckoMeasurement | null
  rightBound: GeckoMeasurement | null
  averageThickness: GeckoMeasurement | null

  // Optional
  inspectionGridDimension?: GeckoMeasurement | null
}

export const FfsCtpPlot: FC<FfsCtpPlotProps> = ({
  criticalPath,
  axis,
  axisLabel,
  minPointIndex,
  leftBound,
  rightBound,
  averageThickness,
  tnom,
  trd,
  tc,
}) => {
  const plotData: Partial<PlotData>[] = useMemo(() => {
    if (criticalPath.length === 0) return []

    const avgValue = averageThickness?.convertTo(WORKING_UNIT_THICKNESS).num
    const leftX = leftBound?.convertTo(WORKING_UNIT_THICKNESS).num
    const rightX = rightBound?.convertTo(WORKING_UNIT_THICKNESS).num

    const criticalPathTrace: Partial<PlotData> = {
      x: criticalPath.map(
        (p) => p.coords[axis].convertTo(WORKING_UNIT_THICKNESS).num
      ),
      y: criticalPath.map(
        (p) => p.value?.convertTo(WORKING_UNIT_THICKNESS).num ?? 0
      ),
      mode: 'lines+markers',
      name: 'Critical Path',
      line: LINES.criticalPath,
      marker: MARKERS.criticalPath,
      hovertemplate: buildHoverTemplate(),
    }

    const deepestPointMarker: Partial<PlotData> | null =
      minPointIndex >= 0 && minPointIndex < criticalPath.length
        ? {
            x: [
              criticalPath[minPointIndex].coords[axis].convertTo(
                WORKING_UNIT_THICKNESS
              ).num,
            ],
            y: [
              criticalPath[minPointIndex].value?.convertTo(
                WORKING_UNIT_THICKNESS
              ).num ?? 0,
            ],
            mode: 'markers' as const,
            name: 'Deepest Point',
            marker: MARKERS.deepestPoint,
            showlegend: true,
            hovertemplate: buildHoverTemplate('Deepest Point'),
          }
        : null

    const averageThicknessLine: Partial<PlotData> | null =
      !isNil(averageThickness) && !isNil(leftBound) && !isNil(rightBound)
        ? {
            x: [leftX!, rightX!],
            y: [avgValue!, avgValue!],
            mode: 'lines' as const,
            name: 't_am (Average)',
            line: LINES.average,
            showlegend: true,
            hovertemplate:
              `<b>Average Thickness</b><br>` +
              `<b>Value:</b> ${avgValue!.toFixed(2)} ${WORKING_UNIT_THICKNESS.name}<br>` +
              '<extra></extra>',
          }
        : null

    return [criticalPathTrace, deepestPointMarker, averageThicknessLine].filter(
      (trace) => trace !== null
    ) as Partial<PlotData>[]
  }, [
    criticalPath,
    minPointIndex,
    averageThickness,
    leftBound,
    rightBound,
    axis,
  ])

  // Set up layout
  const layout: Partial<Layout> = useMemo(() => {
    // Frame Setup
    const yValues = criticalPath.map(
      (p) => p.value?.convertTo(WORKING_UNIT_THICKNESS).num ?? 0
    )
    const allYValues = [
      ...yValues,
      tnom?.convertTo(WORKING_UNIT_THICKNESS).num ?? 0,
      trd?.convertTo(WORKING_UNIT_THICKNESS).num ?? 0,
      tc?.convertTo(WORKING_UNIT_THICKNESS).num ?? 0,
      averageThickness?.convertTo(WORKING_UNIT_THICKNESS).num ?? 0,
    ]

    const yMax = Math.max(...allYValues, 0)

    const xValues = criticalPath.map(
      (p) => p.coords[axis].convertTo(WORKING_UNIT_THICKNESS).num
    )
    const xMin = Math.min(...xValues)
    const xMax = Math.max(...xValues)

    const criticalPathLength = xMax - xMin
    const quarterLength = criticalPathLength * 0.25

    const lHalfExtent =
      leftBound && minPointIndex >= 0 && minPointIndex < criticalPath.length
        ? criticalPath[minPointIndex].coords[axis].convertTo(
            WORKING_UNIT_THICKNESS
          ).num - leftBound.convertTo(WORKING_UNIT_THICKNESS).num
        : 0

    const rHalfExtent =
      rightBound && minPointIndex >= 0 && minPointIndex < criticalPath.length
        ? rightBound.convertTo(WORKING_UNIT_THICKNESS).num -
          criticalPath[minPointIndex].coords[axis].convertTo(
            WORKING_UNIT_THICKNESS
          ).num
        : 0

    const leftExtension = Math.max(quarterLength, lHalfExtent)
    const rightExtension = Math.max(quarterLength, rHalfExtent)

    // Create plot overlays
    const tnomLine: PlotOverlay | null = createFullHorizontalLine(
      xMin,
      xMax,
      leftExtension,
      rightExtension,
      tnom?.convertTo(WORKING_UNIT_THICKNESS).num,
      COLORS.tnom,
      'solid',
      't_nom'
    )

    const trdLine: PlotOverlay | null = createSplitHorizontalLine(
      xMin,
      xMax,
      leftExtension,
      rightExtension,
      trd?.convertTo(WORKING_UNIT_THICKNESS).num,
      COLORS.trd,
      'dash',
      't_rd'
    )

    const tcLine: PlotOverlay | null = createSplitHorizontalLine(
      xMin,
      xMax,
      leftExtension,
      rightExtension,
      tc?.convertTo(WORKING_UNIT_THICKNESS).num,
      COLORS.tc,
      'solid',
      't_c'
    )

    const boundaryLines: PlotOverlay | null = createBoundaryLines(
      leftBound,
      rightBound,
      tnom
    )

    const shapes: Partial<Plotly.Shape>[] = [
      ...(tnomLine?.shapes ?? []),
      ...(trdLine?.shapes ?? []),
      ...(tcLine?.shapes ?? []),
      ...(boundaryLines?.shapes ?? []),
    ]

    const annotations: Partial<Plotly.Annotations>[] = [
      ...(tnomLine?.annotations ?? []),
      ...(trdLine?.annotations ?? []),
      ...(tcLine?.annotations ?? []),
      ...(boundaryLines?.annotations ?? []),
    ]

    return {
      autosize: true,
      height: 500,
      margin: { l: 50, r: 50, b: 100, t: 10 },
      xaxis: {
        title: {
          text: `${axisLabel} Position (${WORKING_UNIT_THICKNESS.name})`,
          font: PLOTLY_FONT,
        },
        tickfont: PLOTLY_FONT,
        gridcolor: CantileverTheme.colors.grayscale[700],
        showgrid: true,
      },
      yaxis: {
        title: {
          text: `Thickness (${WORKING_UNIT_THICKNESS.name})`,
          font: PLOTLY_FONT,
        },
        tickfont: PLOTLY_FONT,
        gridcolor: CantileverTheme.colors.grayscale[700],
        showgrid: true,
        range: [0, yMax * 1.3],
      },
      plot_bgcolor: 'rgba(0, 0, 0, 0)',
      paper_bgcolor: 'rgba(0, 0, 0, 0)',
      font: PLOTLY_FONT,
      showlegend: true,
      legend: {
        x: 1,
        xanchor: 'right',
        y: 1,
        yanchor: 'top',
        font: PLOTLY_FONT,
      },
      shapes,
      annotations,
    }
  }, [
    criticalPath,
    axis,
    axisLabel,
    leftBound,
    rightBound,
    minPointIndex,
    averageThickness,
    tnom,
    trd,
    tc,
  ])

  return criticalPath.length > 0 ? (
    <Plot
      data={plotData}
      layout={layout}
      config={{ displayModeBar: false }}
      style={{ width: '100%', height: '500px' }}
      useResizeHandler
    />
  ) : (
    <div style={{ padding: '40px', textAlign: 'center', color: '#9CA3AF' }}>
      No critical path data available
    </div>
  )
}
